import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import triangleWarning from "assets/icons/triangle_exclamation.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/pro-solid-svg-icons";
import { LoadingButton, Select } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { COMMON_NAMESPACE, PAYABLE_SELECT } from "constants/i18n-namespaces";
import { useNavigate } from "react-router";
import routing from "routing";
import { IBiller } from "types/biller";
import { getLabel } from "utilities/helper";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { getLabels } from "selectors/labels";
import { PayableType } from "types/payable";
import { setSelectedPayableTypeDefinition } from "actions/selectPayableTypeActions";
import { ACTION } from "actions/paymentIntentActions";
import { setPayableSession } from "actions/payableSessionActions";

export interface PayableTypeSelectFormProps {
  biller: IBiller;
}

const SelectPayableTypeForm = (props: PayableTypeSelectFormProps) => {
  const { biller } = props;
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation(PAYABLE_SELECT);

  const navigate = useNavigate();
  const labelSource = useSelector(getLabels) as {
    labels: Record<string, string>;
  };

  const dispatch = useDispatch();

  // Clear the session whenever we land on either the lookups page or payable type selection page.
  useEffect(() => {
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    dispatch(setPayableSession(undefined));

    // This effect only needs to run once on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (biller.payableTypeDefinitions.length === 1) {
      dispatch(
        setSelectedPayableTypeDefinition(biller.payableTypeDefinitions[0])
      );
      // navigating to lookup directly when there is only one payable type
      navigate(routing.lookup.index());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      selectPayableType: null,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      selectPayableType: Yup.object().required(t("payable-type-required")),
    }),
    onSubmit: (values, actions) => {
      setLoading(true);
      if (values.selectPayableType) {
        dispatch(setSelectedPayableTypeDefinition(values.selectPayableType));
        navigate(routing.lookup.index());
      }
      actions.resetForm();
      setSubmitting(false);
    },
  });

  const { errors, touched, setFieldValue, setSubmitting, handleSubmit } =
    formik;

  return (
    <>
      {errors?.selectPayableType && (
        <div className="validate-error">
          <img
            src={triangleWarning}
            alt={`${t("invalid-entry")}`}
            className="validate-error-icon"
          />
          <div>
            <div className="validation-error-header">{t("invalid-entry")}</div>
            <div className="validation-error-message">
              {t("payable-type-required")}
            </div>
          </div>
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="form-container"
        key={i18n.language}
      >
        <div className="payable-select-form">
          <div className="payable-select-info" style={{ paddingBottom: "6px" }}>
            {t("payable-select-info")}
          </div>
          <Select
            name="selectPayableType"
            label={""}
            onChange={(option: { name: string; value: PayableType }[]) => {
              setFieldValue("selectPayableType", option[0]?.value);
            }}
            placeholder={t("select")}
            options={biller.payableTypeDefinitions.map((payable) => ({
              name: getLabel(labelSource.labels, payable.name),
              value: payable,
            }))}
            hasError={!!(touched.selectPayableType && errors.selectPayableType)}
            canDeselect={false}
          />
        </div>

        <div className="select-form-continue-button">
          <LoadingButton
            loadingState="text"
            onClick={() => handleSubmit()}
            className="validate-button"
            type="submit"
            disabled={loading}
          >
            {loading && (
              <div className="button-icon">
                <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
              </div>
            )}
            {!loading && <div>{t("continue", { ns: COMMON_NAMESPACE })}</div>}
          </LoadingButton>
        </div>
      </form>
    </>
  );
};

export default SelectPayableTypeForm;
